export type PerfilAcceso =
  | 'agente_ventas'
  | 'jefe_comercial'
  | 'director'
  | 'administrador'
  | 'soporte';

export type EstadoAcceso = 'habilitado' | 'restringido' | 'bloqueado_mfa';
export type IdentidadCorporativaId =
  | 'corp-admin-01'
  | 'corp-agente-01'
  | 'corp-jefe-01'
  | 'corp-director-01'
  | 'corp-soporte-01';
export type RutTipo = 'definitivo' | 'provisorio';
export type OrigenTipoDerivado = 'propio' | 'lead' | 'campania' | 'otro';
export type AdvertenciaCapturaCodigo =
  | 'afiliacion_vigente'
  | 'gestion_vigente_otro_agente'
  | 'duplicidad_potencial_sin_rut'
  | 'dependencia_no_disponible'
  | 'duplicidad_rut'
  | 'duplicidad_telefono'
  | 'duplicidad_email'
  | 'duplicidad_nombre_apellidos'
  | 'duplicidad_marca_carga_masiva';
export type OrigenCambio = 'Gestor' | 'Sistema' | 'BAV';
export type HistorialProspectoTipoItem =
  | 'asignacion'
  | 'campania'
  | 'gestion'
  | 'oportunidad'
  | 'cierre'
  | 'migracion_inveb'
  | 'campo_historico_desactivado'
  | 'registro_secundario_consolidado'
  | 'advertencia_duplicidad';

export const CANAL_GESTION_DIRECTA_AGENTE_VENTAS = 'gestion-directa-agente-ventas';

export function normalizeCanalId(canal: string | null | undefined): string | null {
  if (!canal) {
    return null;
  }

  const normalized = canal.trim().replace(/^canal-/, '');
  return normalized || null;
}

export interface PermisoSegmentado {
  fuente?: 'administrador' | 'organizacional';
  ambito?: string;
  descripcion?: string;
  canal?: string | null;
  rol?: string;
  region?: string | null;
  agencia?: string | null;
  gerencia?: string | null;
  usuarioObjetivoId?: string | null;
}

export interface ContextoAcceso {
  usuarioId: string;
  perfil: PerfilAcceso;
  permisos: PermisoSegmentado[];
  requiereMfa: boolean;
  discrepanciaDetectada: boolean;
  mfaVerificado: boolean;
  estadoAcceso: EstadoAcceso;
  // Clarificación D-02: la autorización de RUT provisorio se refleja desde backend/contexto.
  autorizacionRutProvisorio?: {
    codigo: string;
    permitido: boolean;
  } | null;
}

export interface IdentidadSinteticaLocal {
  corporateIdentityId: IdentidadCorporativaId;
  label: string;
  perfilEsperado: PerfilAcceso;
  mfaVerificado: boolean;
}

export interface Prospecto {
  id: string;
  nombre: string;
  apellidos: string | null;
  nombreAbreviado?: boolean;
  rut: string | null;
  rutTipo: RutTipo | null;
  rutAutorizacionCodigo: string | null;
  telefono: string | null;
  email: string | null;
  empresa: string | null;
  rutEmpleador?: string | null;
  referidoPor: string | null;
  regionCodigo: string | null;
  comunaCodigo: string | null;
  fechaNacimiento: string | null;
  edad: number | null;
  sistemaPrevisionalActual: string | null;
  observacionesComerciales: string | null;
  origenId: string;
  origenCodigo?: string | null;
  origenTipoDerivado: OrigenTipoDerivado;
  origenDetalle: string | null;
  campaniaId: string | null;
  etapaCodigo: string;
  estadoCodigo: string;
  canalCreacionCodigo: string;
  titularAgenteId: string | null;
  periodoGestionIniciadoAt: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  version: number;
  registroComercialId?: string | null;
  registrosComerciales?: RegistroComercialResumen[];
  consentimientoVigente: boolean;
  consentimientoRegistradoAt: string | null;
  consentimientoRegistradoPor: string | null;
  revisionIncompleta: boolean;
  historial: HistorialProspecto[];
  oportunidadActual?: Oportunidad | null;
  leadId?: string | null;
  fechaRecontacto?: string | null;
  leadCorporativoDesbloqueado?: boolean;
  identificacionSinGestion?: boolean;
  periodoGestionVencido?: boolean;
  ultimaReactivacionEn?: string | null;
  ultimaGestionAt?: string | null;
  etapaNombre?: string | null;
  estadoNombre?: string | null;
  ultimaOportunidad?: UltimaOportunidad | null;
  estadoRegistro?: 'Activo' | 'Cerrado' | 'Consolidado' | null;
  consolidadoEn?: string | null;
  principalConsolidadoId?: string | null;
  posibleDuplicado?: boolean;
  coincidencias?: CoincidenciaDuplicado[];
}

export interface CoincidenciaDuplicado {
  prospectoId: string;
  tipo: string;
  nivel: string;
  nombre: string | null;
  mensaje: string;
  propia: boolean;
}

export interface UltimaOportunidad {
  id: string;
  etapaCodigo: string;
  etapaNombre: string;
  estadoCodigo: string;
  estadoNombre: string;
  cierreTipo: 'perdida' | 'ganada' | null;
  fechaCierre: string | null;
  fechaHabilitadaDesde: string | null;
  elegibleParaNuevaOportunidad: boolean;
}

export interface RegistroComercialResumen {
  id: string | null;
  estadoRegistro: string | null;
  origenCodigo: string | null;
}

export interface ActualizarConsentimientoProspectoRequest {
  vigente: boolean;
  observacion?: string;
}

export interface ActualizarConsentimientoProspectoResponse {
  prospectoId: string;
  consentimientoVigente: boolean;
  consentimientoRegistradoAt: string | null;
  consentimientoRegistradoPor: string | null;
  observacion: string | null;
}

export interface HistorialProspecto {
  fechaHora: string;
  descripcion: string;
  usuario?: string;
  origenCambio?: OrigenCambio;
}

export interface HistorialProspectoApiItem {
  id: string;
  registroComercialId: string | null;
  tipoItem: HistorialProspectoTipoItem;
  titulo: string;
  descripcion: string | null;
  payloadVisible: Record<string, unknown>;
  actorResponsableId: string | null;
  ocurridoAt: string;
  soloLectura: boolean;
}

export interface HistorialProspectoPaginado {
  prospecto: Record<string, unknown> | null;
  items: HistorialProspectoApiItem[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface OrigenComercialVigente {
  id: string;
  codigo: string;
  nombre: string;
  activo: boolean;
  tipoDerivado: OrigenTipoDerivado;
  requiereTextoLibre: boolean;
  requiereCampania: boolean;
}

export interface AdvertenciaCaptura {
  codigo: AdvertenciaCapturaCodigo;
  mensaje: string;
  requiereConfirmacion: boolean;
  bloqueaGuardado: boolean;
}

export interface FiltrosCartera {
  busqueda: string | null;
  etapaCodigo: string | null;
  estadoCodigo: string | null;
  origenId?: string | null;
  page: number;
  size: number;
}

export const ETAPA_CODIGOS_FILTRO = [
  'prospeccion',
  'cotizacion',
  'venta',
  'validacion',
  'declaracion_salud',
  'firma',
  'cierre',
] as const;

export const ESTADO_CODIGOS_FILTRO = [
  'nuevo',
  'asignado',
  'en_gestion',
  'interesado',
  'no_interesado',
  'rechazado',
  'oportunidad_en_cotizacion',
  'propuesta_enviada',
  'decision_pendiente',
  'inicio_venta',
  'flujo_ds_validacion_ok',
  'validacion_fallida_venta',
  'flujo_firma_ds_ok',
  'validacion_fallida_ds',
  'oportunidad_ganada',
  'validacion_fallida_firma',
  'oportunidad_perdida',
  'anulada',
] as const;

/**
 * Traduce una etiqueta o código de etapa/estado al código del contrato BE-10.
 * El código BE de «Declaración de salud» omite el conector «de»
 * (declaracion_salud, no declaracion_de_salud): toPipelineCode no lo sabe,
 * así que se corrige ese único conector antes de validar contra el enum.
 */
export function resolveEnumCode(valor: string | null | undefined, codigosValidos: readonly string[]): string | null {
  const candidato = toPipelineCode(valor);
  if (!candidato) {
    return null;
  }
  if (codigosValidos.includes(candidato)) {
    return candidato;
  }
  const sinConector = candidato.replace(/_de_/g, '_');
  return codigosValidos.includes(sinConector) ? sinConector : null;
}

export interface ItemCarteraProspecto {
  prospectoId: string;
  nombreCompleto: string;
  rut: string | null;
  telefono: string | null;
  email: string | null;
  tieneRegistroVigente?: boolean;
  origenNombre: string;
  origenId?: string | null;
  origenCodigo?: string | null;
  etapaCodigo: string;
  estadoCodigo: string;
  revisionIncompleta: boolean;
  updatedAt: string;
  ultimaGestionAt?: string | null;
  etapaNombre?: string | null;
  estadoNombre?: string | null;
}

export interface EstadoPipelineVigente {
  id: string;
  etapaId: string;
  etapa: string;
  estado: string;
  codigo: string;
  esInicial: boolean;
  esTerminal: boolean;
  origenActualizacion: OrigenCambio;
}

export interface TransicionPipelineVigente {
  desdeEstadoId: string;
  hastaEstadoId: string;
  motivoCatalogoId: string | null;
}

export interface PipelineVigente {
  canalId: string;
  versionId: string;
  vigenciaDesde: string;
  estados: EstadoPipelineVigente[];
  transiciones: TransicionPipelineVigente[];
}

export interface MotivoPerdidaOportunidad {
  id: string;
  nombre: string;
  descripcion: string | null;
  exigeDescripcion: boolean;
  vigenciaDesde: string;
}

export function toPipelineCode(value: string | null | undefined): string {
  const normalized = normalizePipelineComparable(value);

  return normalized ? normalized.replace(/\s+/g, '_') : '';
}

export function normalizePipelineComparable(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function findEstadoPipelineActual(
  estados: EstadoPipelineVigente[],
  etapaActual: string | null | undefined,
  estadoActual: string | null | undefined,
): EstadoPipelineVigente | null {
  const etapaNormalizada = normalizePipelineComparable(etapaActual);
  const estadoNormalizado = normalizePipelineComparable(estadoActual);

  if (!estadoNormalizado) {
    return null;
  }

  const estadosCoincidentes = estados.filter((estado) =>
    [estado.codigo, estado.estado, estado.id].some(
      (valor) => normalizePipelineComparable(valor) === estadoNormalizado,
    ),
  );

  if (estadosCoincidentes.length === 1) {
    return estadosCoincidentes[0] ?? null;
  }

  if (etapaNormalizada) {
    return (
      estadosCoincidentes.find((estado) =>
        [estado.etapa, estado.etapaId, toPipelineCode(estado.etapa)].some(
          (valor) => normalizePipelineComparable(valor) === etapaNormalizada,
        ),
      ) ?? null
    );
  }

  return null;
}

export interface ValidacionTransicion {
  permitida: boolean;
  faltantes: string[];
  reglasIncumplidas: string[];
  vigenciaAplicada: string;
  revisionIncompleta: boolean;
}

export interface TransicionPipeline {
  id: string;
  fechaHora: string;
  origenCambio: OrigenCambio;
  etapaDestino: string;
  estadoDestino: string;
}

export interface Oportunidad {
  id: string;
  prospectoId: string;
  etapaActualId?: string | null;
  estadoActualId?: string | null;
  etapaCodigo?: string | null;
  estadoCodigo?: string | null;
  etapa?: string | null;
  estado?: string | null;
  etapaActual: string;
  estadoActual: string;
  origenUltimoCambio: OrigenCambio;
  editableManualmente: boolean;
  trazabilidad?: HistorialProspecto | null;
  cotizacionSeleccionada?: boolean;
  cotizacionSeleccionadaId?: string | null;
  causalAnulacion?: string | null;
  cierreMotivoNombre?: string | null;
  fechaCierre?: string | null;
  fechaHabilitadaRecupero?: string | null;
  etapaPerdida?: string | null;
}

export function normalizeOportunidad(data: Record<string, unknown> | null | undefined): Oportunidad | null {
  if (!data) {
    return null;
  }

  const pickString = (keys: string[]): string | null => {
    for (const key of keys) {
      const candidate = data[key];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return null;
  };
  const pickBoolean = (keys: string[]): boolean | null => {
    for (const key of keys) {
      const candidate = data[key];
      if (typeof candidate === 'boolean') {
        return candidate;
      }
    }
    return null;
  };
  const pickObject = (keys: string[]): Record<string, unknown> | null => {
    for (const key of keys) {
      const candidate = data[key];
      if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        return candidate as Record<string, unknown>;
      }
    }
    return null;
  };

  const etapaVisible = pickString(['etapaNombre', 'etapa', 'etapaActual', 'etapaCodigo']);
  const estadoVisible = pickString(['estadoNombre', 'estado', 'estadoActual', 'estadoCodigo']);

  return {
    id: pickString(['id']) ?? '',
    prospectoId: pickString(['prospectoId']) ?? '',
    etapaActualId: pickString(['etapaActualId']),
    estadoActualId: pickString(['estadoActualId']),
    etapaCodigo: pickString(['etapaCodigo']),
    estadoCodigo: pickString(['estadoCodigo']),
    etapa: pickString(['etapa']),
    estado: pickString(['estado']),
    etapaActual: etapaVisible ?? '-',
    estadoActual: estadoVisible ?? '-',
    origenUltimoCambio: (pickString(['origenUltimoCambio']) as Oportunidad['origenUltimoCambio']) ?? 'Gestor',
    editableManualmente: pickBoolean(['editableManualmente']) ?? true,
    trazabilidad: (pickObject(['trazabilidad']) as Oportunidad['trazabilidad']) ?? null,
    cotizacionSeleccionada:
      pickBoolean(['cotizacionSeleccionada']) ??
      ('cotizacionSeleccionadaId' in data ? pickString(['cotizacionSeleccionadaId']) !== null : undefined),
    cotizacionSeleccionadaId: 'cotizacionSeleccionadaId' in data ? pickString(['cotizacionSeleccionadaId']) : undefined,
    causalAnulacion: pickString(['causalAnulacion']),
    cierreMotivoNombre: pickString(['cierreMotivoNombre']),
    fechaCierre: pickString(['fechaCierre']),
    fechaHabilitadaRecupero: pickString(['fechaHabilitadaRecupero']),
    etapaPerdida: pickString(['etapaPerdida']),
  };
}

export interface CierreOportunidadPerdida {
  oportunidadId: string;
  motivoId: string;
  descripcion: string | null;
  etapaPerdida: string;
  origenCambio: 'Gestor' | 'Sistema';
  fechaHabilitadaRecupero: string | null;
  programarGestionFutura: boolean;
  fechaProgramada: string | null;
  estadoActual?: string;
  fechaCierre?: string | null;
  leadCorporativoDesbloqueado?: boolean;
  trazabilidad?: HistorialProspecto | null;
}

export interface ElegibilidadRecupero {
  habilitada: boolean;
  fechaHabilitadaDesde: string;
  motivo: string;
  diasEsperaAplicados: number;
}

export interface ProspectoFormData {
  nombre: string;
  apellidos: string;
  rut: string;
  rutTipo: RutTipo;
  telefono: string;
  email: string;
  origenId: string;
  origenDetalle: string;
  campaniaId: string;
  empresa: string;
  rutEmpleador: string;
  referidoPor: string;
  regionCodigo: string;
  comunaCodigo: string;
  fechaNacimiento: string;
  edad: string;
  sistemaPrevisionalActual: string;
  observacionesComerciales: string;
}

export type CampoFormularioProspectoId =
  | 'nombre'
  | 'apellidos'
  | 'rut'
  | 'rutTipo'
  | 'telefono'
  | 'email'
  | 'origenId'
  | 'origenDetalle'
  | 'campaniaId'
  | 'empresa'
  | 'rutEmpleador'
  | 'referidoPor'
  | 'regionCodigo'
  | 'comunaCodigo'
  | 'fechaNacimiento'
  | 'edad'
  | 'sistemaPrevisionalActual'
  | 'observacionesComerciales';

export interface CampoFormularioProspecto {
  visible: boolean;
  obligatorio: boolean;
}

export type MatrizCamposProspecto = Record<CampoFormularioProspectoId, CampoFormularioProspecto>;

export const PROSPECTOS_IDENTITY_STORAGE_KEY = 'catalogos-comerciales.local-identity';

export const IDENTIDADES_SINTETICAS: IdentidadSinteticaLocal[] = [
  {
    corporateIdentityId: 'corp-admin-01',
    label: 'Administrador',
    perfilEsperado: 'administrador',
    mfaVerificado: true,
  },
  {
    corporateIdentityId: 'corp-agente-01',
    label: 'Agente de ventas',
    perfilEsperado: 'agente_ventas',
    mfaVerificado: false,
  },
  {
    corporateIdentityId: 'corp-jefe-01',
    label: 'Jefe comercial',
    perfilEsperado: 'jefe_comercial',
    mfaVerificado: false,
  },
  {
    corporateIdentityId: 'corp-director-01',
    label: 'Director',
    perfilEsperado: 'director',
    mfaVerificado: false,
  },
  {
    corporateIdentityId: 'corp-soporte-01',
    label: 'Soporte',
    perfilEsperado: 'soporte',
    mfaVerificado: false,
  },
];

export const DEFAULT_FILTROS_CARTERA: FiltrosCartera = {
  busqueda: null,
  etapaCodigo: null,
  estadoCodigo: null,
  origenId: null,
  page: 0,
  size: 25,
};

export const DEFAULT_VALIDACION_TRANSICION: ValidacionTransicion = {
  permitida: true,
  faltantes: [],
  reglasIncumplidas: [],
  vigenciaAplicada: '',
  revisionIncompleta: false,
};

export const DEFAULT_PROSPECTO_FORM_DATA: ProspectoFormData = {
  nombre: '',
  apellidos: '',
  rut: '',
  rutTipo: 'definitivo',
  telefono: '',
  email: '',
  origenId: '',
  origenDetalle: '',
  campaniaId: '',
  empresa: '',
  rutEmpleador: '',
  referidoPor: '',
  regionCodigo: '',
  comunaCodigo: '',
  fechaNacimiento: '',
  edad: '',
  sistemaPrevisionalActual: '',
  observacionesComerciales: '',
};
